/**
 * Campaign URL Query Parameter Utility
 *
 * Extracts and identifies campaign codes from the current URL.
 * Used across signup/payment flows to drive campaign-specific logic
 */

const CAMPAIGN_QUERY_PARAM = 'campaign';
const TEN_BDAY_CAMPAIGN_CODE = '10BDAY';
const WINBACK_CAMPAIGN_CODE = 'WINBACK_REACTIVATION';
const FREE_MONTH_CAMPAIGN_CODE = 'FREE_MONTH_REACTIVATION';
const DISCOUNT_ROLLOVER_CAMPAIGN_CODE = 'DISCOUNT_ROLLOVER_REACTIVATION';
const REACTIVATION_CAMPAIGN_CODE = WINBACK_CAMPAIGN_CODE;
const CAMPAIGN_SESSION_KEY = 'equ_campaign_code';

export interface CampaignInfo {
  hasCampaign: boolean;
  campaignCode: string | null;
  is10BDayCampaign: boolean;
  isWinbackCampaign: boolean;
  isReactivationCampaign: boolean;
  isFreeMonthCampaign: boolean;
  isDiscountRolloverCampaign: boolean;
}

/**
 * Allowed campaign codes for payment intent and invoice purchase payloads.
 * Must be one of: FREE_MONTH_REACTIVATION, DISCOUNT_ROLLOVER_REACTIVATION.
 */
const ALLOWED_PAYMENT_CAMPAIGN_CODES = [
  FREE_MONTH_CAMPAIGN_CODE,
  DISCOUNT_ROLLOVER_CAMPAIGN_CODE,
] as const;

export type AllowedPaymentCampaignCode =
  (typeof ALLOWED_PAYMENT_CAMPAIGN_CODES)[number];

/**
 * Validates and returns the campaign code if it is one of the allowed campaign codes
 * [FREE_MONTH_REACTIVATION, DISCOUNT_ROLLOVER_REACTIVATION].
 * Returns null for any other scenario / code.
 */
const getAllowedCampaignCode = (
  code?: string | null
): AllowedPaymentCampaignCode | null => {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  if (
    upper === FREE_MONTH_CAMPAIGN_CODE ||
    upper === DISCOUNT_ROLLOVER_CAMPAIGN_CODE
  ) {
    return upper as AllowedPaymentCampaignCode;
  }
  return null;
};

/** Returns true for any campaign code that routes to /reactivate-subscription */
export const isReactivationCampaignCode = (code: string | null): boolean => {
  const upper = code?.trim().toUpperCase();
  return (
    upper === WINBACK_CAMPAIGN_CODE ||
    upper === FREE_MONTH_CAMPAIGN_CODE ||
    upper === DISCOUNT_ROLLOVER_CAMPAIGN_CODE
  );
};

export const getCampaignFromUrl = (): CampaignInfo => {
  const searchParams = new URLSearchParams(window.location.search);
  const campaignCode = searchParams.get(CAMPAIGN_QUERY_PARAM);

  const hasCampaign = !!campaignCode && campaignCode.trim().length > 0;
  const normalizedCode = hasCampaign ? campaignCode!.trim() : null;
  const is10BDayCampaign =
    normalizedCode?.toUpperCase() === TEN_BDAY_CAMPAIGN_CODE;
  const isWinbackCampaign =
    normalizedCode?.toUpperCase() === WINBACK_CAMPAIGN_CODE;
  const isFreeMonthCampaign =
    normalizedCode?.toUpperCase() === FREE_MONTH_CAMPAIGN_CODE;
  const isDiscountRolloverCampaign =
    normalizedCode?.toUpperCase() === DISCOUNT_ROLLOVER_CAMPAIGN_CODE;

  return {
    hasCampaign,
    campaignCode: normalizedCode,
    is10BDayCampaign,
    isWinbackCampaign,
    isReactivationCampaign:
      isWinbackCampaign || isFreeMonthCampaign || isDiscountRolloverCampaign,
    isFreeMonthCampaign,
    isDiscountRolloverCampaign,
  };
};

/**
 * Parse a campaign code from an arbitrary URL search string and persist it
 * to sessionStorage. This is the low-level primitive used by the higher-level
 * helpers below.
 */
const saveCampaignFromSearchString = (search: string): void => {
  try {
    const params = new URLSearchParams(search);
    const code = params.get(CAMPAIGN_QUERY_PARAM);
    if (code && code.trim().length > 0) {
      persistCampaignToSession(code.trim());
    }
  } catch {
    // no-op
  }
};

/**
 * Persist the campaign code from an SSO URL as early as possible — before any
 * logout or redirect — so it survives session transitions.
 *
 * Checks two places:
 *  1. The top-level SSO query string (e.g. `?campaign=WINBACK_REACTIVATION`).
 *  2. The `redirect` param's own query string
 *     (e.g. `/reactivate-subscription?campaign=WINBACK_REACTIVATION`).
 *
 * @param ssoSearch     - `location.search` from the SSO URL.
 * @param redirectParam - The raw value of the `redirect` query param.
 */
export const persistCampaignFromSSOUrl = (
  ssoSearch: string,
  redirectParam: string
): void => {
  // 1. Check the SSO URL itself
  saveCampaignFromSearchString(ssoSearch);

  // 2. Check inside the redirect path's own query string
  if (!redirectParam) return;
  try {
    const redirectUrl = new URL(redirectParam, window.location.origin);
    saveCampaignFromSearchString(redirectUrl.search);
  } catch {
    // redirectParam is a relative path — extract the search manually
    const qIndex = redirectParam.indexOf('?');
    if (qIndex !== -1) {
      saveCampaignFromSearchString(redirectParam.slice(qIndex));
    }
  }
};

/** Checks if a given campaign code matches the winback/reactivation campaign code */
export const isWinbackCampaignCode = (code: string | null): boolean => {
  return !!code && code.trim().toUpperCase() === WINBACK_CAMPAIGN_CODE;
};

/** Save the campaign code to sessionStorage (survives same-tab navigations) */
export const persistCampaignToSession = (campaignCode: string): void => {
  try {
    sessionStorage.setItem(CAMPAIGN_SESSION_KEY, campaignCode);
  } catch {
    // sessionStorage may be unavailable in private browsing on some browsers
  }
};

/** Read the campaign code from sessionStorage */
export const getCampaignFromSession = (): string | null => {
  try {
    return sessionStorage.getItem(CAMPAIGN_SESSION_KEY);
  } catch {
    return null;
  }
};

/** Clear the campaign code from sessionStorage (call after redemption) */
export const clearCampaignFromSession = (): void => {
  try {
    sessionStorage.removeItem(CAMPAIGN_SESSION_KEY);
  } catch {
    // no-op
  }
};

export {
  CAMPAIGN_QUERY_PARAM,
  TEN_BDAY_CAMPAIGN_CODE,
  WINBACK_CAMPAIGN_CODE,
  FREE_MONTH_CAMPAIGN_CODE,
  DISCOUNT_ROLLOVER_CAMPAIGN_CODE,
  REACTIVATION_CAMPAIGN_CODE,
  CAMPAIGN_SESSION_KEY,
  ALLOWED_PAYMENT_CAMPAIGN_CODES,
  getAllowedCampaignCode,
};
